import {
  BarChart3,
  Building2,
  LayoutGrid,
  Plus,
  SquarePen,
  StickyNote,
  UserPlus,
  Users,
  Building,
} from "lucide-react";
import { useTranslate } from "ra-core";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useLocation, useMatch } from "react-router";

import { CompanyCreateSheet } from "../companies/CompanyCreateSheet";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";
import { hapticTick } from "./haptic";
import { useMobileChrome } from "./MobileChrome";
import { MOBILE_MORE_PATH } from "./mobileModules";
import {
  PlusBackdrop,
  PlusMenu,
  type PlusMenuItem,
} from "./plus-menu/PlusMenu";
import { MobileTabs } from "./tab-bar/MobileTabs";
import { MOBILE_TABS } from "./tab-bar/tabs";

type CreateSheet = "contact" | "company" | "task" | "note";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();
  const { menuOpen, setMenuOpen } = useMobileChrome();
  const contactId = useMatch("/contacts/:id/*")?.params.id;
  const [sheet, setSheet] = useState<CreateSheet | null>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, setMenuOpen]);

  const closeMenu = useCallback(() => setMenuOpen(false), [setMenuOpen]);

  const toggleMenu = () => {
    if (!menuOpen) hapticTick();
    setMenuOpen(!menuOpen);
  };

  const sheetProps = (name: CreateSheet) => ({
    open: sheet === name,
    onOpenChange: (open: boolean) => setSheet(open ? name : null),
  });

  const primary: PlusMenuItem[] = [
    {
      label: translate("resources.contacts.action.new"),
      Icon: UserPlus,
      run: () => setSheet("contact"),
    },
    {
      label: translate("resources.companies.action.new"),
      Icon: Building,
      run: () => setSheet("company"),
    },
    {
      label: translate("crm.mobile.plus.new_task"),
      Icon: SquarePen,
      run: () => setSheet("task"),
    },
    {
      label: translate("crm.mobile.plus.new_note"),
      Icon: StickyNote,
      run: () => setSheet("note"),
    },
  ];

  const secondary: PlusMenuItem[] = [
    {
      label: translate("resources.contacts.name", { smart_count: 2 }),
      Icon: Users,
      to: "/contacts",
    },
    {
      label: translate("resources.companies.name", { smart_count: 2 }),
      Icon: Building2,
      to: "/companies",
    },
    // Tasks and deals are tabs; reports left the bar for here.
    {
      label: translate("crm.mobile.nav_reports"),
      Icon: BarChart3,
      to: "/informes",
    },
    {
      label: translate("crm.mobile.nav_more"),
      Icon: LayoutGrid,
      to: MOBILE_MORE_PATH,
    },
  ];

  return (
    <>
      {sheet === "contact" ? (
        <ContactCreateSheet {...sheetProps("contact")} />
      ) : null}
      {sheet === "company" ? (
        <CompanyCreateSheet {...sheetProps("company")} />
      ) : null}
      {sheet === "note" ? (
        <NoteCreateSheet {...sheetProps("note")} contact_id={contactId} />
      ) : null}
      {sheet === "task" ? (
        <TaskCreateSheet {...sheetProps("task")} contact_id={contactId} />
      ) : null}
      <PlusBackdrop open={menuOpen} onClose={closeMenu} />
      <nav
        aria-label={translate("crm.navigation.label")}
        data-menu-open={menuOpen ? "" : undefined}
        data-mobile-tabbar
        className="mobile-nav fixed inset-x-0 bottom-0 z-50 px-3"
        style={{ "--mobile-tab-count": MOBILE_TABS.length } as CSSProperties}
      >
        {/* The capsule and the + are centred together, as one unit. */}
        <div className="mx-auto mb-[var(--mobile-nav-bottom)] flex w-fit items-end gap-2">
          <PlusMenu
            id={menuId}
            open={menuOpen}
            onClose={closeMenu}
            primary={primary}
            secondary={secondary}
            toggleRef={toggleRef}
          >
            <MobileTabs />
          </PlusMenu>
          <button
            ref={toggleRef}
            type="button"
            className="plus-toggle liquid-glass glass-press relative z-10 flex size-[var(--mobile-pill)] shrink-0 items-center justify-center rounded-full text-[var(--glass-accent)] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-controls={menuId}
            aria-label={
              menuOpen
                ? translate("crm.mobile.nav_close")
                : translate("crm.mobile.quick_actions")
            }
            onClick={toggleMenu}
          >
            <Plus className="size-6" strokeWidth={2.25} />
          </button>
        </div>
      </nav>
    </>
  );
};
